import { Primitive } from '@radix-ui/react-primitive';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const chip = tailwindVariants({
  base: 'inline-flex items-center justify-between gap-1 truncate font-medium',
  compoundVariants: [
    {
      appearance: 'filled',
      className: 'bg-black text-white',
      color: 'black',
    },
    {
      appearance: 'outline',
      className: 'border-black text-black',
      color: 'black',
    },
    {
      appearance: 'filled',
      className: 'bg-blue-5 text-blue-12',
      color: 'blue',
    },
    {
      appearance: 'outline',
      className: 'border-blue-5 text-blue-9',
      color: 'blue',
    },
    {
      appearance: 'filled',
      className: 'bg-green-7 text-teal-12',
      color: 'green',
    },
    {
      appearance: 'outline',
      className: 'border-green-5 text-green-9',
      color: 'green',
    },
    {
      appearance: 'filled',
      className: 'bg-grey-4 text-grey-12',
      color: 'grey',
    },
    {
      appearance: 'outline',
      className: 'border-grey-5 text-grey-9',
      color: 'grey',
    },
    {
      appearance: 'filled',
      className: 'bg-blue-3 text-blue-12',
      color: 'light-blue',
    },
    {
      appearance: 'outline',
      className: 'border-blue-5 text-blue-9',
      color: 'light-blue',
    },
    {
      appearance: 'filled',
      className: 'bg-green-4 text-green-12',
      color: 'light-green',
    },
    {
      appearance: 'outline',
      className: 'border-green-5 text-green-9',
      color: 'light-green',
    },
    {
      appearance: 'filled',
      className: 'bg-grey-4 text-grey-12',
      color: 'light-grey',
    },
    {
      appearance: 'outline',
      className: 'border-grey-3 text-grey-9',
      color: 'light-grey',
    },
    {
      appearance: 'filled',
      className: 'bg-orange-2 text-orange-12',
      color: 'light-orange',
    },
    {
      appearance: 'outline',
      className: 'border-orange-3 text-orange-9',
      color: 'light-orange',
    },
    {
      appearance: 'filled',
      className: 'bg-pink-2 text-pink-12',
      color: 'light-pink',
    },
    {
      appearance: 'outline',
      className: 'border-pink-3 text-pink-9',
      color: 'light-pink',
    },
    {
      appearance: 'filled',
      className: 'bg-purple-2 text-purple-12',
      color: 'light-purple',
    },
    {
      appearance: 'outline',
      className: 'border-purple-3 text-purple-9',
      color: 'light-purple',
    },
    {
      appearance: 'filled',
      className: 'bg-red-4 text-red-12',
      color: 'light-red',
    },
    {
      appearance: 'outline',
      className: 'border-red-3 text-red-9',
      color: 'light-red',
    },
    {
      appearance: 'filled',
      className: 'bg-teal-4 text-teal-12',
      color: 'light-teal',
    },
    {
      appearance: 'outline',
      className: 'border-teal-3 text-teal-9',
      color: 'light-teal',
    },
    {
      appearance: 'filled',
      className: 'bg-yellow-3 text-yellow-12',
      color: 'light-yellow',
    },
    {
      appearance: 'outline',
      className: 'border-yellow-3 text-yellow-9',
      color: 'light-yellow',
    },
    {
      appearance: 'filled',
      className: 'bg-orange-4 text-orange-12',
      color: 'orange',
    },
    {
      appearance: 'outline',
      className: 'border-orange-5 text-orange-9',
      color: 'orange',
    },
    {
      appearance: 'filled',
      className: 'bg-pink-4 text-pink-12',
      color: 'pink',
    },
    {
      appearance: 'outline',
      className: 'border-pink-5 text-pink-9',
      color: 'pink',
    },
    {
      appearance: 'filled',
      className: 'bg-purple-6 text-purple-12',
      color: 'purple',
    },
    {
      appearance: 'outline',
      className: 'border-purple-5 text-purple-9',
      color: 'purple',
    },
    {
      appearance: 'filled',
      className: 'bg-red-7 text-red-12',
      color: 'red',
    },
    {
      appearance: 'outline',
      className: 'border-red-5 text-red-9',
      color: 'red',
    },
    {
      appearance: 'filled',
      className: 'bg-teal-6 text-teal-12',
      color: 'teal',
    },
    {
      appearance: 'outline',
      className: 'border-teal-5 text-teal-9',
      color: 'teal',
    },
    {
      appearance: 'filled',
      className: 'bg-transparent text-grey-12',
      color: 'transparent',
    },
    {
      appearance: 'outline',
      className: 'border-transparent text-grey-12',
      color: 'transparent',
    },
    {
      appearance: 'filled',
      className: 'bg-yellow-6 text-yellow-12',
      color: 'yellow',
    },
    {
      appearance: 'outline',
      className: 'border-yellow-5 text-yellow-9',
      color: 'yellow',
    },
    {
      class: 'px-2',
      shape: 'rounded',
      size: 'small',
    },
    {
      class: 'px-3',
      shape: 'rounded',
      size: ['medium', 'large', 'extra-large'],
    },
    {
      className: 'px-1',
      shape: 'rectangular',
      size: 'small',
    },
    {
      className: 'px-1.5',
      shape: 'rectangular',
      size: ['medium', 'large', 'extra-large'],
    },
  ],
  defaultVariants: {
    appearance: 'filled',
    color: 'grey',
    shape: 'rectangular',
    size: 'small',
  },
  variants: {
    appearance: {
      filled: 'border-0',
      outline: 'border',
    },
    color: {
      black: '',
      blue: '',
      green: '',
      grey: '',
      'light-blue': '',
      'light-green': '',
      'light-grey': '',
      'light-orange': '',
      'light-pink': '',
      'light-purple': '',
      'light-red': '',
      'light-teal': '',
      'light-yellow': '',
      orange: '',
      pink: '',
      purple: '',
      red: '',
      teal: '',
      transparent: '',
      yellow: '',
    },
    shape: {
      rectangular: 'rounded-4',
      rounded: 'rounded-full',
    },
    size: {
      'extra-large': 'h-8 text-14',
      large: 'h-7 text-14',
      medium: 'h-6 text-12',
      small: 'h-5 text-10',
    },
  },
});

export type ChipVariants = VariantProps<typeof chip>;

export type ChipProps = Omit<ComponentPropsWithoutRef<typeof Primitive.div>, keyof ChipVariants> & ChipVariants;

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  ({ appearance, className, color = 'grey', shape = 'rectangular', size = 'small', ...restOfProps }, forwardedRef) => {
    return (
      <Primitive.div
        className={chip({ appearance, className, color, shape, size })}
        data-testid="CHIP"
        {...restOfProps}
        ref={forwardedRef}
      />
    );
  },
);

Chip.displayName = 'Chip';
